

import { useRef, useEffect, useId } from 'react';
import { LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useHaptics } from '@/hooks/useHaptics';
import { getNflTeam } from '@/config/nfl-teams';

export interface RoundStep {
  id: string;
  title: string;
  status: string;
  round: number;
  /** Total matches in this round. When provided (> 0), renders a pick-progress dash row. */
  gameCount?: number;
  /** Matches the user has picked a winner for in this round. */
  picksMade?: number;
  /**
   * STANDARD: the entrant's settled score for a COMPLETED round. When set (and
   * the round is COMPLETED), the pill's second row shows "✓ {points}" instead
   * of the pick-progress dashes.
   */
  completedPoints?: number | null;
  /**
   * Card leagues (BEST_BALL / DRAFT_PPR): the entrant's weekly points.
   * "✓ {points}" once the week is official (finalized Wednesday); a
   * provisional dot + points until then.
   */
  bestBallWeek?: { points: number; isOfficial: boolean } | null;
}

export interface SurvivorRoundPick {
  team: string;
  result: 'PENDING' | 'WON' | 'LOST' | 'VOID';
}

interface RoundStepperProps {
  rounds: RoundStep[];
  currentRoundId?: string;
  eventId: string;
  onNavigate: (undercardId: string) => void;
  /** When false, renders inline (no sticky top bar / full-width chrome). Defaults to true. */
  sticky?: boolean;
  /**
   * SURVIVOR: per-round pick state keyed by round number. When provided,
   * replaces the pick-progress dash row with a single result glyph per round.
   */
  survivorPicks?: Map<number, SurvivorRoundPick>;
  /** SURVIVOR: rounds after the elimination week render dimmed but stay tappable. */
  eliminatedWeek?: number | null;
}

/** Short team label for the survivor pick chip (NFL abbr, else first 4 chars). */
function survivorAbbrev(team: string): string {
  return getNflTeam(team)?.abbr ?? team.slice(0, 4).toUpperCase();
}

/**
 * The active pill's gold plate. Rendered inside the active pill only and shared
 * across pills by layoutId, so selecting a week slides the plate rather than
 * hard-cutting it. Sits behind the pill content (content wrapper is z-10).
 */
const ACTIVE_PLATE = 'absolute inset-0 rounded-lg bg-gold/15 border border-gold/30 pointer-events-none';

/** Slide feel for the shared plate — settles at roughly --duration-context. */
const PLATE_SPRING = { type: 'spring', stiffness: 420, damping: 38, mass: 0.8 } as const;

const STATUS_DOT: Record<string, string> = {
  COMPLETED: 'bg-verdigris',
  SCORING: 'bg-orange-400',
  LOCKED: 'bg-gold',
  OPEN: 'bg-verdigris',
  UPCOMING: 'bg-surface-4',
};

export function RoundStepper({ rounds, currentRoundId, eventId, onNavigate, sticky = true, survivorPicks, eliminatedWeek }: RoundStepperProps) {
  const activeRef = useRef<HTMLButtonElement>(null);
  // Namespaces the shared layoutId per stepper instance, so a second rail on the
  // same screen (sticky + inline) can never steal this one's plate.
  const railId = useId();
  const reducedMotion = useReducedMotion();
  const { fire } = useHaptics();

  // Centre the active week. Reduced motion jumps instead of gliding — the rail
  // honours the preference everywhere else, and a smooth scroll is exactly the
  // kind of unrequested travel the setting exists to stop.
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [currentRoundId, reducedMotion]);

  if (rounds.length === 0) return null;

  const track = (
    <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto scrollbar-hide py-2.5 sm:py-3">
      <LayoutGroup id={railId}>
        {rounds.map((round) => {
          const isActive = round.id === currentRoundId;
          const isCompleted = round.status === 'COMPLETED' || round.status === 'SCORING';
          const dotColor = STATUS_DOT[round.status] || STATUS_DOT.UPCOMING;
          const gameCount = round.gameCount ?? 0;
          const picksMade = Math.min(round.picksMade ?? 0, gameCount);

          return (
            <div key={round.id} className="flex items-center flex-shrink-0">
              <motion.button
                ref={isActive ? activeRef : undefined}
                onClick={() => {
                  fire('selection');
                  onNavigate(round.id);
                }}
                whileTap={reducedMotion ? undefined : { scale: 0.96 }}
                transition={{ duration: 0.12, ease: 'easeOut' }}
                className={cn(
                  'relative flex flex-col gap-1 w-28 sm:w-32 px-3 sm:px-4 py-2 rounded-lg text-[11px] sm:text-xs font-medium transition-colors',
                  isActive
                    ? 'text-gold border border-transparent'
                    : isCompleted
                      ? 'text-warm-gray border border-surface-3 hover:text-cream hover:bg-surface-2'
                      : 'text-warm-gray/60 border border-surface-3 hover:text-cream hover:bg-surface-2',
                  // SURVIVOR: rounds the entrant never reached — dimmed but still tappable.
                  eliminatedWeek != null && round.round > eliminatedWeek && 'opacity-40'
                )}
              >
                {/* Active plate — shared element, slides in from the previous pill. */}
                {isActive &&
                  (reducedMotion ? (
                    <span className={ACTIVE_PLATE} />
                  ) : (
                    <motion.span layoutId="week-rail-active" transition={PLATE_SPRING} className={ACTIVE_PLATE} />
                  ))}
                <span className="relative z-10 flex flex-col gap-1 w-full min-w-0">
                  <span className="flex items-center gap-1.5 sm:gap-2 w-full min-w-0">
                    {round.status === 'UPCOMING' ? (
                      /* Not yet open — padlock (wireframe), not a bare dimmed dot. */
                      <svg className="w-2.5 h-2.5 flex-shrink-0 text-warm-gray/50" viewBox="0 0 10 12" fill="none" aria-label="Not yet open">
                        <rect x="1" y="5" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.2" />
                        <path d="M3 5V3.5a2 2 0 0 1 4 0V5" stroke="currentColor" strokeWidth="1.2" />
                      </svg>
                    ) : (
                      <span className={cn('w-2 h-2 rounded-full flex-shrink-0', dotColor)} />
                    )}
                    <span className={cn(
                      'uppercase tracking-wider truncate min-w-0',
                      isActive ? 'font-bold' : 'font-medium'
                    )}>
                      {round.title}
                    </span>
                  </span>
                  {/* SURVIVOR: one glyph per round — ✓ won, ✗ lost, — void, team chip
                      for a pending pick, hollow dot when no pick was made. */}
                  {survivorPicks ? (
                    <span className="flex items-center w-full pl-3.5 h-3">
                      {(() => {
                        const sp = survivorPicks.get(round.round);
                        if (!sp) return <span className="w-2 h-2 rounded-full border border-warm-gray/40" />;
                        if (sp.result === 'WON') return <span className="text-verdigris-text text-[11px] leading-none font-bold">✓</span>;
                        if (sp.result === 'LOST') return <span className="text-oxblood-text text-[11px] leading-none font-bold">✗</span>;
                        if (sp.result === 'VOID') return <span className="text-warm-gray/50 text-[11px] leading-none font-bold">—</span>;
                        return (
                          <span className="px-1 py-px rounded bg-surface-3 text-[9px] leading-none font-bold">
                            {survivorAbbrev(sp.team)}
                          </span>
                        );
                      })()}
                    </span>
                  ) : round.bestBallWeek != null ? (
                    /* Card leagues: weekly points — ✓ once official, amber
                       provisional dot while stats can still move (finalizes Wed). */
                    <span className="flex items-center gap-1 w-full pl-3.5 h-3">
                      {round.bestBallWeek.isOfficial ? (
                        <span className="text-verdigris-text text-[11px] leading-none font-bold">✓</span>
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse shrink-0" />
                      )}
                      <span className="text-[10px] leading-none tabular-nums font-semibold opacity-90">
                        {round.bestBallWeek.points}
                      </span>
                    </span>
                  ) : round.status === 'COMPLETED' && round.completedPoints != null ? (
                    /* STANDARD, settled round: the entrant's score for the week. */
                    <span className="flex items-center gap-1 w-full pl-3.5 h-3">
                      <span className="text-verdigris-text text-[11px] leading-none font-bold">✓</span>
                      <span className="text-[10px] leading-none tabular-nums font-semibold opacity-90">
                        {round.completedPoints}
                      </span>
                    </span>
                  ) : gameCount > 0 && (
                    /* Per-match pick progress — gold dash = picked, cream dash = not yet,
                       with a picked/total count. Dashes flex to fill the remaining width. */
                    <span className="flex items-center gap-1.5 w-full pl-3.5">
                      <span className="flex items-center gap-0.5 flex-1 min-w-0">
                        {Array.from({ length: gameCount }).map((_, i) => (
                          <span
                            key={i}
                            className={cn('h-0.5 flex-1 min-w-0 rounded-full', i < picksMade ? 'bg-gold' : 'bg-cream/40')}
                          />
                        ))}
                      </span>
                      <span className="flex-shrink-0 text-[10px] leading-none tabular-nums font-semibold opacity-80">
                        {picksMade}/{gameCount}
                      </span>
                    </span>
                  )}
                </span>
              </motion.button>
            </div>
          );
        })}
      </LayoutGroup>
    </div>
  );

  if (!sticky) return track;

  return (
    <div className="sticky top-[6.25rem] z-20 bg-surface/95 backdrop-blur-sm border-b border-surface-3 shadow-[0_8px_16px_-8px_rgba(0,0,0,0.5)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {track}
      </div>
    </div>
  );
}
